import axios from 'axios';
import type { AxiosPromise } from 'axios';
import type { IAuthService, ISessionRequest, IUserRequest } from '@/data/_autogen/IValveVista';
import {
  generateMockGuestTokenResponse,
  generateMockLoginResponse,
  generateMockRegistrationResponse,
  generateMockSessionResponse,
  delayResponse,
  isValidEmail,
  isValidPassword,
  isEmailRegistered
} from '@/mocks/authMocks';

const API_BASE_URL = process.env.VUE_APP_API_URL || '/api';
const USE_MOCK_API = false;//process.env.VUE_APP_USE_MOCK_API === 'true'

/**
 * AuthService - Handles all authentication API calls
 * Uses static methods similar to CartService pattern
 * Methods match IAuthService interface signatures
 */
export default class AuthService {
  /**
   * Request a guest token for unauthenticated users
   * POST /api/v1/auth/guest-token
   * @returns Promise resolving to guest session with token
   */
  static getGuestToken(): AxiosPromise<ISessionRequest> {
    if (USE_MOCK_API) {
      return this.mockGetGuestToken();
    }

    return axios.get<ISessionRequest>(`${API_BASE_URL}/v1/auth/guest-token`);
  }

  /**
   * Register a new user account
   * POST /api/v1/auth/register-user
   * @param userRequest - User registration data
   * @returns Promise resolving to success message
   */
  static registerUser(userRequest: IUserRequest): AxiosPromise<string> {
    if (USE_MOCK_API) {
      return this.mockRegisterUser(userRequest);
    }

    return axios.post<string>(`${API_BASE_URL}/v1/auth/register-user`, userRequest);
  }

  /**
   * Login existing user
   * POST /api/v1/auth/login
   * @param userRequest - User credentials (userName/email + password)
   * @returns Promise resolving to session with access token
   */
  static login(userRequest: IUserRequest): AxiosPromise<ISessionRequest> {
    if (USE_MOCK_API) {
      return this.mockLogin(userRequest);
    }

    return axios.post<ISessionRequest>(`${API_BASE_URL}/v1/auth/login`, userRequest);
  }

  /**
   * Logout current user
   * POST /api/v1/auth/logout
   * @returns Promise resolving to success message
   */
  static logout(): AxiosPromise<string> {
    if (USE_MOCK_API) {
      return this.mockLogout();
    }

    return axios.post<string>(`${API_BASE_URL}/v1/auth/logout`);
  }

  /**
   * Get current session information
   * GET /api/v1/auth/me
   * @returns Promise resolving to current session data
   */
  static me(): AxiosPromise<ISessionRequest> {
    if (USE_MOCK_API) {
      return this.mockMe();
    }

    return axios.get<ISessionRequest>(`${API_BASE_URL}/v1/auth/me`);
  }

  // ============================================================================
  // MOCK IMPLEMENTATIONS
  // ============================================================================

  /**
   * Mock: Request guest token
   */
  private static mockGetGuestToken(): AxiosPromise<ISessionRequest> {
    return new Promise((resolve) => {
      delayResponse(800).then(() => {
        const response = generateMockGuestTokenResponse();
        resolve({
          data: response,
          status: 200,
          statusText: 'OK',
          headers: {},
          config: {} as any,
        });
      });
    });
  }

  /**
   * Mock: Register user
   */
  private static mockRegisterUser(userRequest: IUserRequest): AxiosPromise<string> {
    return new Promise((resolve, reject) => {
      delayResponse(1000).then(() => {
        // Validate input
        if (!userRequest.emailAddress || !isValidEmail(userRequest.emailAddress)) {
          reject(
            axios.AxiosError.from({
              message: 'Invalid email format',
              code: '400',
              config: {} as any,
              response: {
                status: 400,
                statusText: 'Bad Request',
                data: { error: 'Invalid email address' },
                headers: {},
                config: {} as any,
              },
            })
          );
          return;
        }

        if (!userRequest.password || !isValidPassword(userRequest.password)) {
          reject(
            axios.AxiosError.from({
              message: 'Password does not meet requirements',
              code: '400',
              config: {} as any,
              response: {
                status: 400,
                statusText: 'Bad Request',
                data: {
                  error: 'Password must be at least 8 characters with uppercase, lowercase, and number',
                },
                headers: {},
                config: {} as any,
              },
            })
          );
          return;
        }

        if (isEmailRegistered(userRequest.emailAddress)) {
          reject(
            axios.AxiosError.from({
              message: 'Email already registered',
              code: '409',
              config: {} as any,
              response: {
                status: 409,
                statusText: 'Conflict',
                data: { error: 'Email address already registered' },
                headers: {},
                config: {} as any,
              },
            })
          );
          return;
        }

        resolve({
          data: 'User registered successfully',
          status: 201,
          statusText: 'Created',
          headers: {},
          config: {} as any,
        });
      });
    });
  }

  /**
   * Mock: Login user
   */
  private static mockLogin(userRequest: IUserRequest): AxiosPromise<ISessionRequest> {
    return new Promise((resolve, reject) => {
      delayResponse(800).then(() => {
        const identifier = userRequest.userName || userRequest.emailAddress;

        if (!identifier || !userRequest.password) {
          reject(
            axios.AxiosError.from({
              message: 'Missing credentials',
              code: '400',
              config: {} as any,
              response: {
                status: 400,
                statusText: 'Bad Request',
                data: { error: 'Email/username and password required' },
                headers: {},
                config: {} as any,
              },
            })
          );
          return;
        }

        const response = generateMockLoginResponse(identifier);

        if (!response) {
          reject(
            axios.AxiosError.from({
              message: 'Invalid credentials',
              code: '401',
              config: {} as any,
              response: {
                status: 401,
                statusText: 'Unauthorized',
                data: { error: 'Invalid username/email or password' },
                headers: {},
                config: {} as any,
              },
            })
          );
          return;
        }

        resolve({
          data: response,
          status: 200,
          statusText: 'OK',
          headers: {},
          config: {} as any,
        });
      });
    });
  }

  /**
   * Mock: Logout user
   */
  private static mockLogout(): AxiosPromise<string> {
    return new Promise((resolve) => {
      delayResponse(500).then(() => {
        resolve({
          data: 'Logged out successfully',
          status: 200,
          statusText: 'OK',
          headers: {},
          config: {} as any,
        });
      });
    });
  }

  /**
   * Mock: Get current session
   */
  private static mockMe(): AxiosPromise<ISessionRequest> {
    return new Promise((resolve) => {
      // In real implementation, would get token from request header
      // For mock, return a default authenticated user
      const mockToken = 'mock_jwt_token_12345';
      const response = generateMockSessionResponse(mockToken, false);

      resolve({
        data: response,
        status: 200,
        statusText: 'OK',
        headers: {},
        config: {} as any,
      });
    });
  }
}
